import {
  Shield01Icon,
  UserMultiple02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ROLE_PERMISSIONS, USER_PERMISSIONS } from 'shared';
import { RolesPermissions } from '@/components/features/settings/roles-permissions';
import { UserManagement } from '@/components/features/settings/user-management';
import { RequirePermission } from '@/components/shared/RequirePermission';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

function ManagementPageLoading() {
  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-96" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <div className="space-y-4">
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/user-management')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tab: (search.tab as string) || 'users',
      subTab: (search.subTab as string) || 'users',
    };
  },
  pendingComponent: ManagementPageLoading,
  component: () => (
    <Suspense fallback={<ManagementPageLoading />}>
      <ManagementPage />
    </Suspense>
  ),
});

function ManagementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab, subTab } = useSearch({ from: '/_authenticated/user-management' });
  const [, setRefreshTrigger] = useState(0);

  const refreshUserData = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleTabChange = (value: string) => {
    navigate({
      to: '/user-management',
      search: { tab: value, subTab: 'users' },
    });
  };

  const handleSubTabChange = (value: string) => {
    navigate({
      to: '/user-management',
      search: {
        tab: tab || 'users',
        subTab: value,
      },
    });
  };

  return (
    <RequirePermission permission={[USER_PERMISSIONS.READ, ROLE_PERMISSIONS.READ]}>
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
            {t('userManagement.title')}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {t('userManagement.description')}
          </p>
        </div>

        <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="flex flex-wrap w-full overflow-x-auto">
            <TabsTrigger value="users" className="text-xs sm:text-sm">
              <div className="flex items-center gap-1 sm:gap-2">
                <HugeiconsIcon icon={UserMultiple02Icon} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{t('userManagement.tabs.users')}</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="roles" className="text-xs sm:text-sm">
              <div className="flex items-center gap-1 sm:gap-2">
                <HugeiconsIcon icon={Shield01Icon} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden md:inline">
                  {t('userManagement.tabs.rolesPermissions')}
                </span>
                <span className="md:hidden">{t('userManagement.tabs.roles')}</span>
              </div>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-4">
            {tab === 'users' && (
              <UserManagement
                activeTab={subTab || 'users'}
                onSubTabChange={handleSubTabChange}
                onDataChange={refreshUserData}
              />
            )}
          </TabsContent>

          <TabsContent value="roles" className="mt-4">
            {tab === 'roles' && <RolesPermissions />}
          </TabsContent>
        </Tabs>
      </div>
    </RequirePermission>
  );
}
